import { css } from '@emotion/react';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Avatar, AvatarProps, Text, Truncated } from '@frontend/design-system';
import { PROVIDER_COLUMN_MIN_WIDTH } from '../constants';
import { moveUnassignedProviderHeading } from '../utils';

const ProviderAvatar = (props: Omit<AvatarProps, 'size'>) => {
  return (
    <Avatar
      {...props}
      isUser
      size='small'
      css={{
        placeSelf: 'center',
        justifyContent: 'center',
      }}
    />
  );
};

const ProviderName = ({ name, archived }: { name: string; archived?: boolean }) => {
  const { t } = useTranslation('schedule');

  const nameWithStatus = `${name} ${archived ? `(${t('INACTIVE')})` : ''}`.trim();
  return (
    <Text
      textAlign='center'
      size='medium'
      weight='bold'
      css={[{ maxWidth: `calc(${PROVIDER_COLUMN_MIN_WIDTH}px - ${theme.spacing(3)})` }]}
      color={archived ? 'subdued' : 'default'}
    >
      <Truncated tooltipOptions={{ hoverDelay: 300 }}>{nameWithStatus}</Truncated>
    </Text>
  );
};

const UnassignedProvider = ({ name }: { name: string }) => {
  const capitalizedProviderName = name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <>
      <Icon name='help' color='subdued' height={32} width={32} css={styles.unassignProviderIcon} />
      <Text size='medium' textAlign='center' weight='bold'>
        {capitalizedProviderName}
      </Text>
    </>
  );
};

type OfficeProviders = {
  providersData: {
    name: string;
    src: string;
    eventCount: number;
    providerId: string;
    locationId: string;
    archived?: boolean;
  }[];
};

export const OfficeProviders = ({ providersData }: OfficeProviders) => {
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  // NOTE: This sorts the providers so that the unassigned provider is always at the end of the list
  const sortedProviders = moveUnassignedProviderHeading(providersData);
  const { navigate } = useSettingsNavigate();

  const handleClick = (locationId: string, providerId: string) => {
    navigate({
      to: '/schedule/manage-providers/:locationId/edit/:providerId',
      params: { locationId, providerId },
    });
  };

  return (
    <>
      {sortedProviders.map(({ name, src, eventCount, providerId, locationId, archived }, idx) => (
        <section key={name + idx} css={styles.listContainer}>
          {name.toLowerCase() === 'unassigned' ? (
            <UnassignedProvider name={name} />
          ) : (
            <>
              {archived ? (
                <div
                  css={{
                    background: theme.colors.neutral10,
                    borderRadius: theme.borderRadius.full,
                    padding: theme.spacing(0.5),
                  }}
                >
                  <Icon name='archive' color='subdued' />
                </div>
              ) : (
                <ProviderAvatar
                  name={name}
                  src={src}
                  onClick={isDebugModeOn ? () => handleClick(locationId, providerId) : undefined}
                />
              )}
              <ProviderName name={name} archived={archived} />
            </>
          )}
          {isDebugModeOn && !!eventCount && (
            <Text size='small' weight='bold' css={styles.counterStyle}>
              {eventCount}
            </Text>
          )}
        </section>
      ))}
    </>
  );
};

const styles = {
  unassignProviderIcon: css({
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    background: theme.colors.neutral5,
    borderRadius: theme.borderRadius.medium,
  }),
  listContainer: css({
    position: 'relative',
    minWidth: PROVIDER_COLUMN_MIN_WIDTH,
    padding: theme.spacing(1, 1.5),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  }),
  counterStyle: css({
    borderRadius: theme.borderRadius.medium,
    background: theme.colors.primary5,
    border: `2px solid ${theme.colors.primary10}`,
    boxShadow: `${theme.shadows.innerRight}, ${theme.shadows.light}`,
    position: 'absolute',
    left: `calc(50% + ${theme.spacing(1)})`,
    top: `calc(50% - ${theme.spacing(1.25)})`,
    lineHeight: 1,
    padding: theme.spacing(0, 0.5),
  }),
};
